import React, { Component } from 'react';
import '../styles/Thread.css';
import { getThreads } from '../services/threadService';
import {
  Box,
  Paper,
  Stack,
  Typography,
  TextField,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Pagination,
  CircularProgress,
  Tooltip,
  Collapse,
  IconButton
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

class Threads extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hits: [],
      page: 1,
      total_elem: 0,
      isLoading: false,
      q: '',
      expanded: {} // { threadId: boolean }
    };
    this.onSearchSubmit = this.onSearchSubmit.bind(this);
    this.onPageChange = this.onPageChange.bind(this);
    this.fetchPage = this.fetchPage.bind(this);
    this.toggleExpand = this.toggleExpand.bind(this);
  }

  componentDidMount() {
    this._isMounted = true;
    this.fetchPage(1, '');
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  onSearchSubmit(e) {
    e.preventDefault();
    this.fetchPage(1, this.state.q);
  }

  onPageChange(e, value) {
    this.fetchPage(value, this.state.q);
  }

  fetchPage(page, q) {
    this.setState({ isLoading: true });
    getThreads(q || '', page)
      .then((result) => {
        if (!this._isMounted) return;
        this.setState({
          hits: result.threads || [],
          page: parseFloat(result.page_nbr) || page,
          total_elem: parseFloat(result.total_elem) || 0,
          isLoading: false
        });
      })
      .catch(() => this.setState({ isLoading: false }));
  }

  toggleExpand(id) {
    this.setState((prev) => ({ expanded: { ...prev.expanded, [id]: !prev.expanded[id] } }));
  }

  render() {
    const { hits, page, total_elem, isLoading, q, expanded } = this.state;
    const pageCount = Math.max(1, Math.ceil((total_elem || 0) / 10));

    return (
      <Box sx={{ p: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Typography variant="h6">Pages GC</Typography>
          <Box component="form" onSubmit={this.onSearchSubmit} sx={{ display: 'flex', gap: 1, width: { xs: '100%', sm: 'auto' } }}>
            <TextField
              size="small"
              placeholder="Rechercher…"
              value={q}
              onChange={(e) => this.setState({ q: e.target.value })}
            />
            <Button variant="contained" type="submit">Search</Button>
          </Box>
        </Stack>

        <Paper variant="outlined">
          <TableContainer sx={{ maxHeight: '70vh' }}>
            <Table
              size="medium"
              stickyHeader
              sx={{
                tableLayout: 'fixed',
                '& tbody tr:nth-of-type(odd)': { bgcolor: 'action.hover' }
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell />
                  <TableCell>Id</TableCell>
                  <TableCell>Title</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading && hits.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : (
                  hits.map((item) => {
                    const open = !!expanded[item._id];
                    return (
                      <React.Fragment key={item._id}>
                        <TableRow hover>
                          <TableCell width={48} sx={{ py: 0 }}>
                            <IconButton size="small" onClick={() => this.toggleExpand(item._id)} aria-label={open ? 'Réduire' : 'Développer'}>
                              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                            </IconButton>
                          </TableCell>
                          <TableCell sx={{ width: { xs: 160, md: 280 } }}>
                            <Tooltip title={item._id}>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
                                {item._id}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell sx={{ maxWidth: 480 }}>
                            <Tooltip title={item.page_title || ''}>
                              <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {item.page_title}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={3}>
                            <Collapse in={open} timeout="auto" unmountOnExit>
                              <Box sx={{ p: 2 }}>
                                <Typography variant="subtitle2" gutterBottom>Détails</Typography>
                                <Stack direction="row" spacing={4} flexWrap="wrap">
                                  <Typography variant="body2"><b>uid:</b> {item.uid || ''}</Typography>
                                  <Typography variant="body2"><b>full_id:</b> {item.full_id || ''}</Typography>
                                  <Typography variant="body2"><b>identifier:</b> {item.identifier || ''}</Typography>
                                  <Typography variant="body2"><b>nbr comments N1:</b> {item.child_comments?.length || 0}</Typography>
                                  <Typography variant="body2"><b>lifetime front:</b> {item.lifetime || ''}</Typography>
                                  <Typography variant="body2"><b>publication_date:</b> {item.publication_date || ''}</Typography>
                                  <Typography variant="body2"><b>manager:</b> {item.manager || ''}</Typography>
                                </Stack>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })
                )}
                {!isLoading && hits.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      <Typography variant="body2" color="text.secondary">Aucune donnée</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="caption" color="text.secondary">Page {page} / {pageCount}</Typography>
            <Pagination
              page={page}
              count={pageCount}
              onChange={this.onPageChange}
              size="small"
              color="primary"
              showFirstButton
              showLastButton
              siblingCount={0}
              boundaryCount={1}
            />
          </Box>
        </Paper>
      </Box>
    );
  }
}

export default Threads;